import {
  FlaskConical, FileStack, Factory, Package, ScrollText, RefreshCcw, Droplets, Sparkles, Sun, Leaf, ShieldCheck,
  Beaker, Scissors, Wind, HeartHandshake, Layers, ClipboardCheck, Truck, BadgeCheck, Microscope, FileText, Globe2, Timer, TestTubes,
} from "lucide-react";

const U = (id) => `https://images.unsplash.com/${id}?auto=format&fit=crop&q=80&w=1200`;
const LAB_LINE =
  "https://static.prod-images.emergentagent.com/jobs/9444b1cb-2cbd-41d7-b086-be1e8cd6725c/images/4fb15ce5eaa6d3df2d85a543c40cce8eeb3edf7bc67c174c283e868dbe9c2bd8.jpeg";
const COSMETICS_IMG =
  "https://images.unsplash.com/photo-1773565744218-d8d11de58362?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwzfHx3ZWxsbmVzcyUyMHNwYSUyMGFlc3RoZXRpYyUyMGJlYXV0eSUyMHByb2R1Y3RzfGVufDB8fHx8MTc4NDk5Njk3OHww&ixlib=rb-4.1.0&q=85";
const AYURVEDA_IMG =
  "https://images.unsplash.com/photo-1636740599648-ae84f705fc2e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxsYWJvcmF0b3J5JTIwZ2xhc3N3YXJlJTIwbHV4dXJ5JTIwY29zbWV0aWMlMjBmb3JtdWxhdGlvbnxlbnwwfHx8fDE3ODQ5OTY5Nzh8MA&ixlib=rb-4.1.0&q=85";

export const SERVICE_PAGES = {
  "/services": {
    testId: "services-page",
    crumbs: [{ label: "Services" }],
    hero: {
      overline: "Our Services",
      title: "Everything between",
      titleEm: "idea and launch.",
      intro:
        "The Formulation House works as your outsourced product development team — formulating, testing, sourcing manufacturing and preparing documentation so your brand can launch a product that performs, complies and sells.",
      image: LAB_LINE,
      imageAlt: "Formulation lab bench, texture development and a filling line",
    },
    features: {
      overline: "What We Do",
      title: "Six services,",
      titleEm: "one accountable partner.",
      items: [
        { icon: FlaskConical, title: "Custom Formula Development", text: "Original cosmetic, Ayurvedic and nutraceutical formulations built from scratch around your brief, benchmark products and target cost." },
        { icon: FileStack, title: "Stock & Ready-to-Launch Formulas", text: "A curated library of proven, stability-tested formulas you can brand and launch quickly — ideal for private-label and first-range launches." },
        { icon: Factory, title: "Manufacturing Coordination", text: "Scale-up, tech transfer and batch production through our network of licensed manufacturing facilities in India." },
        { icon: Package, title: "Packaging Guidance", text: "Component selection, compatibility and stability testing, and shelf-presence advice so your pack protects and sells the formula." },
        { icon: ScrollText, title: "Testing & Regulatory Support", text: "Stability, safety and microbiology testing coordination, label and claims review, and documentation for Indian and export compliance." },
        { icon: RefreshCcw, title: "Reformulation", text: "Improve cost, stability, texture, fragrance or compliance of an existing product without losing what customers already love about it." },
      ],
    },
    split: {
      overline: "Who It's For",
      title: "Built for brands at",
      titleEm: "every stage.",
      paragraphs: [
        "Startups and D2C founders use us to turn a concept into a shelf-ready product without hiring an in-house R&D team. Established beauty, wellness and FMCG brands use us as an agile specialist partner for new categories, formats and export markets.",
        "Healthcare professionals and Ayurvedic companies work with our doctor-led team to build trusted, authentic product lines — classical or proprietary — with modern sensorials and documentation.",
      ],
      bullets: ["Startups & D2C brands", "Beauty & wellness brands", "Healthcare professionals", "Ayurvedic companies", "Established FMCG enterprises", "International & export businesses"],
      image: U("photo-1581093577421-f561a654a353"),
      imageAlt: "Formulation scientist working at a laboratory bench",
    },
    steps: {
      overline: "How We Work",
      title: "From first call to",
      titleEm: "finished product.",
      items: [
        { title: "Discovery call", text: "Share your product vision, target customer, timeline and goals with our team." },
        { title: "Requirement understanding", text: "We map your brand, budget, category and regulatory requirements in detail before any lab work begins." },
        { title: "Formula development", text: "Our expert formulators craft your formula and iterate on texture, actives, fragrance and cost." },
        { title: "Sample evaluation", text: "You review physical samples against your benchmarks and expectations; we refine until it is right." },
        { title: "Formula finalisation & testing", text: "The formula is locked and moved through stability, safety and compatibility testing." },
        { title: "Manufacturing (if applicable)", text: "Commercial production through our licensed manufacturing partners with hands-on tech transfer." },
        { title: "Launch", text: "Your finished product is documented, compliant and ready to ship and sell." },
      ],
    },
    faqs: [
      { q: "Do you work on a single product or full ranges?", a: "Both. Many clients start with one hero product and expand into a range once the first formula and manufacturing route is proven." },
      { q: "Can you work with an existing manufacturer?", a: "Yes. We can develop the formula and hand it over to your manufacturer with full tech-transfer documentation, or coordinate production through our own licensed partner network." },
      { q: "Is my formula confidential?", a: "Every engagement is NDA-protected. Custom formulas developed for you belong to your brand." },
      { q: "Which markets do you support?", a: "We formulate and document for India and for export markets, with regulatory guidance tailored to each destination." },
    ],
    related: ["/cosmetic-formulation-development", "/cosmetic-manufacturing-support", "/regulatory-testing-support"],
  },

  "/cosmetic-formulation-development": {
    testId: "cosmetic-page",
    crumbs: [{ label: "Services", path: "/services" }, { label: "Cosmetic Formulation" }],
    hero: {
      overline: "Cosmetic Formulation Development",
      title: "Cosmetic formulations with",
      titleEm: "elegant textures and proven stability.",
      intro:
        "We develop cosmetic and personal-care formulations — face and body care, cleansers, lotions, balms, soaps and more — built on modern actives, premium sensorials and stability you can stand behind, for the Indian market and export.",
      image: COSMETICS_IMG,
      imageAlt: "Premium cosmetic products styled with botanicals",
    },
    features: {
      overline: "Formats We Formulate",
      title: "Every cosmetic format,",
      titleEm: "engineered from the bench.",
      items: [
        { icon: Droplets, title: "Creams, Lotions & Emulsions", text: "Light gel-creams to rich body butters — stable emulsions with the slip, absorption and after-feel your positioning demands." },
        { icon: Sparkles, title: "Cleansers & Body Wash", text: "Sulphate-free and mild-surfactant systems, foaming gels, cream cleansers, syndet bars and hand-crafted soaps." },
        { icon: Sun, title: "Sun & Day Care Bases", text: "Cosmetically elegant daily-wear bases and SPF-ready systems developed with compatible actives and pigments." },
        { icon: Leaf, title: "Herbal & Ayurveda-Inspired Cosmetics", text: "Botanical extracts and classical Ayurvedic ingredients delivered in modern, shelf-stable cosmetic formats." },
        { icon: Beaker, title: "Actives & Delivery", text: "Vitamin C, retinoids, peptides, ceramides, niacinamide, acids and plant actives — selected and stabilised for real efficacy." },
        { icon: ShieldCheck, title: "Preservation & Stability", text: "Modern preservative systems, challenge testing and accelerated stability so the product stays safe and beautiful in use." },
      ],
    },
    split: {
      overline: "Our Approach",
      title: "Formulation-first,",
      titleEm: "commercially grounded.",
      paragraphs: [
        "Every project begins with a detailed brief: your target customer, benchmark products, claims, price point and market. We translate this into an ingredient strategy and cost architecture before the first lab trial.",
        "Our chemists and Ayurvedic doctors then iterate on texture, actives, fragrance and pack compatibility, sharing physical samples at each round until the formula matches your vision and your margins.",
      ],
      bullets: ["Bespoke or ready-to-launch formulas", "Target-cost led ingredient selection", "Clean, vegan and herbal positioning", "Fragrance and colour development", "Packaging compatibility testing", "Export-ready documentation"],
      image: U("photo-1619451427882-6aaaded0cc61"),
      imageAlt: "Personal care products on a minimal backdrop",
    },
    steps: {
      overline: "Development Path",
      title: "How a cosmetic product",
      titleEm: "comes to life.",
      items: [
        { title: "Brief & benchmarking", text: "We study your reference products and define texture, claims, INCI preferences and cost targets." },
        { title: "Ingredient strategy", text: "Actives, emollients, emulsifiers and preservation are selected for performance, compliance and supply security." },
        { title: "Lab trials & samples", text: "Multiple trial batches are evaluated with you for feel, fragrance, colour and performance." },
        { title: "Stability & compatibility", text: "Accelerated stability, freeze-thaw and pack compatibility studies protect the product through its shelf life." },
        { title: "Scale-up & manufacturing", text: "The locked formula is transferred to a licensed manufacturer for pilot and commercial batches." },
      ],
    },
    faqs: [
      { q: "What is the minimum order for a custom cosmetic formulation?", a: "Formulation development itself has no volume requirement. Manufacturing minimums depend on the format and facility; we advise you on realistic MOQs early in the project." },
      { q: "Can you match an existing product?", a: "We can benchmark a reference product and develop a formula that achieves a similar or improved sensorial and performance profile, while keeping your formula original." },
      { q: "Do you provide the INCI list and label copy?", a: "Yes. Every finalised formula comes with its INCI list, and our regulatory team supports label review and claims guidance." },
    ],
    related: ["/skincare-formulation-development", "/haircare-formulation-development", "/private-label-cosmetics-india"],
  },

  "/skincare-formulation-development": {
    testId: "skincare-page",
    crumbs: [{ label: "Services", path: "/services" }, { label: "Skincare Formulation" }],
    hero: {
      overline: "Skincare Formulation Development",
      title: "Actives-led skincare,",
      titleEm: "built to perform.",
      intro:
        "From serums and moisturisers to cleansers, masks and treatment products, we formulate skincare that delivers visible results with the textures and stability premium brands need — for Indian skin, Indian climate and export markets.",
      image: U("photo-1585945037805-5fd82c2e60b1"),
      imageAlt: "Skincare cream texture swatch",
    },
    features: {
      overline: "Skincare Formats",
      title: "A complete",
      titleEm: "skincare routine.",
      items: [
        { icon: Droplets, title: "Serums & Ampoules", text: "Water-light, oil and emulsion serums carrying niacinamide, vitamin C, hyaluronic acid, peptides, retinoids and plant actives." },
        { icon: Sparkles, title: "Moisturisers & Night Creams", text: "Gel-creams for humid climates through to barrier-repair ceramide creams and rich overnight treatments." },
        { icon: Beaker, title: "Cleansers, Toners & Exfoliants", text: "Gentle foaming and cream cleansers, essence toners, AHA/BHA/PHA exfoliants and enzyme masks." },
        { icon: Layers, title: "Masks & Treatments", text: "Clay, sheet-mask essences, overnight masks, spot treatments and targeted under-eye care." },
        { icon: Sun, title: "Day Care & SPF-Ready Bases", text: "Cosmetically elegant daily-wear bases developed for compatibility with UV filters and pigments." },
        { icon: Leaf, title: "Ayurvedic & Herbal Skincare", text: "Kumkumadi, saffron, turmeric, licorice, manjistha and other classical botanicals in modern, stable formats." },
      ],
    },
    split: {
      overline: "Why It Works",
      title: "Efficacy, sensorials",
      titleEm: "and stability — together.",
      paragraphs: [
        "Great skincare has to do three things at once: deliver actives at effective, stable levels; feel beautiful on skin; and stay safe and consistent for its entire shelf life. Our formulators design for all three from the first trial.",
        "We select and stabilise actives at meaningful concentrations, pair them with delivery systems and pH ranges that keep them effective, and validate the result through stability, compatibility and safety testing.",
      ],
      bullets: ["Clinically-informed active levels", "pH and delivery optimisation", "Non-comedogenic, fragrance-free options", "Dermatologically mindful preservation", "Clean, vegan and cruelty-free positioning", "Claims support and documentation"],
      image: U("photo-1581093577421-f561a654a353"),
      imageAlt: "Formulation chemist preparing a skincare trial batch",
    },
    steps: {
      overline: "Development Path",
      title: "From concept to",
      titleEm: "cabinet-ready.",
      items: [
        { title: "Skin concern & claim mapping", text: "We define the target concern, hero actives, supporting claims and the customer's expected sensorial profile." },
        { title: "Active selection & stabilisation", text: "Concentrations, chelators, antioxidants and pH windows are set so actives remain potent through shelf life." },
        { title: "Texture development", text: "Multiple trial batches refine slip, absorption, finish and layering behaviour under real routines." },
        { title: "Testing", text: "Stability, preservative efficacy, pack compatibility and safety testing are coordinated before launch." },
        { title: "Manufacturing & launch", text: "Tech transfer to a licensed facility, first commercial batch and launch documentation." },
      ],
    },
    faqs: [
      { q: "Can you formulate for sensitive or acne-prone skin?", a: "Yes. We regularly develop fragrance-free, non-comedogenic and barrier-supporting formulas with gentle preservation systems." },
      { q: "How long does skincare development take?", a: "A typical serum or moisturiser reaches a locked formula in a few weeks of iterative trials; stability testing then runs in parallel with packaging and manufacturing planning." },
      { q: "Do you help with claims like 'brightening' or 'anti-ageing'?", a: "We formulate to support the claims you want to make and guide you on substantiation and compliant wording for your market." },
    ],
    related: ["/cosmetic-formulation-development", "/haircare-formulation-development", "/regulatory-testing-support"],
  },

  "/haircare-formulation-development": {
    testId: "haircare-page",
    crumbs: [{ label: "Services", path: "/services" }, { label: "Haircare Formulation" }],
    hero: {
      overline: "Haircare Formulation Development",
      title: "Scalp-first haircare,",
      titleEm: "from cleanse to shine.",
      intro:
        "We develop haircare and scalp-care products — shampoos, conditioners, hair oils, serums, masks and treatments — that balance performance, sensorials and mildness, drawing on modern surfactant science and Ayurvedic hair traditions.",
      image: U("photo-1562322140-8baeececf3df"),
      imageAlt: "Healthy, styled hair after salon care",
    },
    features: {
      overline: "Haircare Formats",
      title: "Every step of the",
      titleEm: "hair routine.",
      items: [
        { icon: Droplets, title: "Shampoos & Cleansers", text: "Sulphate-free, mild-surfactant and clarifying systems with the foam, rinse-off and manageability customers expect." },
        { icon: Scissors, title: "Conditioners & Masks", text: "Rinse-off and leave-in conditioners, deep-repair masks and bond-supporting treatments." },
        { icon: Leaf, title: "Hair Oils & Ayurvedic Oils", text: "Classical and proprietary hair oils — bhringraj, amla, brahmi, coconut and sesame bases — developed by Ayurvedic doctors." },
        { icon: Wind, title: "Serums, Sprays & Stylers", text: "Anti-frizz serums, heat protectants, leave-in mists and light styling formats." },
        { icon: Sparkles, title: "Scalp Treatments", text: "Anti-dandruff, scalp-soothing and hair-fall-support formulations with targeted actives." },
        { icon: ShieldCheck, title: "Mild & Clean Positioning", text: "Sulphate-, silicone- and paraben-free options with modern preservation and compatible fragrances." },
      ],
    },
    split: {
      overline: "Our Approach",
      title: "Performance you can",
      titleEm: "feel in the shower.",
      paragraphs: [
        "Haircare is judged in seconds — foam, slip, rinse and comb-through. We build surfactant and conditioning systems around those moments, then validate them on real hair and against your benchmark products.",
        "For herbal and Ayurvedic ranges, our doctors formulate authentic oils and treatments, while our chemists ensure the finished product is stable, pleasant to use and compliant for your markets.",
      ],
      bullets: ["Sulphate-free surfactant systems", "Silicone and silicone-free conditioning", "Ayurvedic hair oil expertise", "Fragrance development", "Wet and dry comb evaluation", "Salon and spa formats"],
      image: U("photo-1519823551278-64ac92734fb1"),
      imageAlt: "Spa hair and body care products",
    },
    steps: {
      overline: "Development Path",
      title: "How we develop",
      titleEm: "haircare.",
      items: [
        { title: "Hair type & concern brief", text: "We define the target hair type, concern, claims and the sensory benchmarks the product must meet." },
        { title: "System design", text: "Surfactant, conditioning, active and fragrance systems are designed around mildness and performance targets." },
        { title: "Trial batches", text: "Foam, viscosity, slip and rinse-off are refined through iterative samples evaluated with you." },
        { title: "Stability & testing", text: "Viscosity stability, preservative efficacy and pack compatibility are validated." },
        { title: "Manufacturing & launch", text: "Scale-up and production through licensed partners, with documentation for launch." },
      ],
    },
    faqs: [
      { q: "Can you develop a sulphate-free shampoo that still foams well?", a: "Yes. Modern mild surfactant blends can deliver rich, creamy foam with excellent rinse-off; we tune the system to your desired feel." },
      { q: "Do you formulate classical Ayurvedic hair oils?", a: "Our Ayurvedic doctors develop both classical preparations and proprietary blends, with the documentation required for Ayurvedic or cosmetic positioning." },
      { q: "Can you reformulate our existing shampoo to reduce cost?", a: "Reformulation is one of our core services — we can optimise cost, stability or compliance while protecting the sensorial customers love." },
    ],
    related: ["/skincare-formulation-development", "/wellness-product-development", "/cosmetic-manufacturing-support"],
  },

  "/wellness-product-development": {
    testId: "wellness-page",
    crumbs: [{ label: "Services", path: "/services" }, { label: "Wellness Product Development" }],
    hero: {
      overline: "Wellness Product Development",
      title: "Wellness oils and rituals,",
      titleEm: "developed by doctors.",
      intro:
        "We develop wellness products — aromatherapy and massage oils, body and bath oils, balms, roll-ons, wellness teas and Ayurveda-inspired rituals — combining traditional knowledge with modern formulation, safety and documentation.",
      image: U("photo-1521146250551-a5578dcc2e64"),
      imageAlt: "Wellness oils and botanicals arranged for a ritual",
    },
    features: {
      overline: "Wellness Formats",
      title: "Formats for sleep, stress",
      titleEm: "and everyday rituals.",
      items: [
        { icon: Droplets, title: "Aromatherapy & Massage Oils", text: "Essential-oil blends and carrier-oil systems for relaxation, sleep, energy and muscle-relief rituals." },
        { icon: Leaf, title: "Ayurvedic Oils & Abhyanga", text: "Classical and proprietary Ayurvedic oils for body, head and foot rituals, developed by Ayurvedic doctors." },
        { icon: HeartHandshake, title: "Balms, Roll-Ons & Rubs", text: "Solid balms and roll-on formats for headaches, tension, sleep and travel — stable across Indian climates." },
        { icon: Sparkles, title: "Bath & Body Rituals", text: "Bath oils, salts, body oils and scrubs for premium at-home and spa experiences." },
        { icon: Sun, title: "Wellness Teas & Infusions", text: "Herbal tea and infusion blends for sleep, digestion, immunity and calm, with sourcing and compliance guidance." },
        { icon: Globe2, title: "Spa & Hospitality Ranges", text: "Signature amenity and treatment ranges for spas, salons, hotels and wellness retreats." },
      ],
    },
    split: {
      overline: "Our Approach",
      title: "Tradition, validated by",
      titleEm: "science.",
      paragraphs: [
        "Wellness products live or die on authenticity and experience. Our Ayurvedic doctors design blends around genuine traditional logic — dosha, season and intent — while our formulators make sure each product is stable, safe and consistent from batch to batch.",
        "Fragrance, texture and pack all matter here, so we prototype the whole ritual, not just the liquid: how the oil pours, warms, absorbs and lingers.",
      ],
      bullets: ["Essential-oil safety and dilution", "Classical Ayurvedic references", "Carrier oil and antioxidant systems", "Sensorial and fragrance design", "Spa and hospitality formats", "Export documentation"],
      image: AYURVEDA_IMG,
      imageAlt: "Ayurvedic ingredients and glassware",
    },
    steps: {
      overline: "Development Path",
      title: "From ritual concept to",
      titleEm: "finished blend.",
      items: [
        { title: "Ritual & positioning brief", text: "We define the intent — sleep, calm, energy, recovery — the target user and the experience you want to create." },
        { title: "Blend design", text: "Doctors and formulators select botanicals, essential oils and carriers with safe, effective usage levels." },
        { title: "Prototypes", text: "Samples are evaluated for aroma, feel, absorption and stability, and refined with you." },
        { title: "Testing & documentation", text: "Stability, safety and labelling documentation prepared for your target markets." },
        { title: "Production & launch", text: "Manufacturing through licensed partners with packaging guidance for premium presentation." },
      ],
    },
    faqs: [
      { q: "Can wellness oils be positioned as Ayurvedic products?", a: "Yes, where the formulation and documentation support it. We advise on Ayurvedic versus cosmetic positioning and the documentation each requires." },
      { q: "Do you develop products for spas and hotels?", a: "We create signature amenity and treatment ranges for hospitality and spa clients, including minis and kits." },
      { q: "Are essential-oil products safe for all users?", a: "We formulate within established safety and dilution guidelines and advise on appropriate usage instructions and warnings." },
    ],
    related: ["/haircare-formulation-development", "/private-label-cosmetics-india", "/regulatory-testing-support"],
  },

  "/private-label-cosmetics-india": {
    testId: "private-label-page",
    crumbs: [{ label: "Services", path: "/services" }, { label: "Private Label Cosmetics India" }],
    hero: {
      overline: "Private Label & Contract Development · India",
      title: "Launch your brand in India,",
      titleEm: "faster and smarter.",
      intro:
        "Private-label and contract product-development support for beauty, wellness and Ayurvedic brands in India — proven ready-to-launch formulas, bespoke development where you need differentiation, and licensed manufacturing partners to take you to market.",
      image: U("photo-1552046122-03184de85e08"),
      imageAlt: "Minimal private label cosmetic bottles",
    },
    features: {
      overline: "How We Support You",
      title: "Two routes to",
      titleEm: "market.",
      items: [
        { icon: FileStack, title: "Ready-to-Launch Formulas", text: "Proven, stability-tested skincare, haircare, body care and wellness formulas you can brand and launch with minimal development time." },
        { icon: FlaskConical, title: "Custom Private-Label Development", text: "Bespoke formulas for your hero products where differentiation, unique actives or a signature texture matter." },
        { icon: Factory, title: "Licensed Manufacturing in India", text: "Production through our network of licensed cosmetic and Ayurvedic manufacturing facilities, with realistic MOQs." },
        { icon: Package, title: "Packaging & Presentation", text: "Component sourcing guidance, compatibility checks and shelf-presence advice for your first range." },
        { icon: ClipboardCheck, title: "Compliance & Labelling", text: "Indian regulatory guidance, label review and documentation so your range is ready for marketplaces and retail." },
        { icon: Globe2, title: "Export-Ready Foundations", text: "Formulas and documentation prepared with future export markets in mind." },
      ],
    },
    split: {
      overline: "Why Private Label With Us",
      title: "Speed without",
      titleEm: "sacrificing quality.",
      paragraphs: [
        "Most private-label suppliers offer a fixed catalogue and little flexibility. We start from a formulation-first standpoint: you can launch on proven formulas today, and upgrade to bespoke development as your brand grows — without changing partners.",
        "Because our team includes Ayurvedic doctors and cosmetic formulators, you can build a coherent range across cosmetics, Ayurveda and wellness under one roof, with consistent quality and documentation.",
      ],
      bullets: ["Curated ready-to-launch library", "Upgrade path to custom formulas", "Licensed manufacturing partners", "Realistic minimum order quantities", "Marketplace-ready documentation", "Cosmetics, Ayurveda and wellness under one roof"],
      image: U("photo-1613803745799-ba6c10aace85"),
      imageAlt: "Cosmetic packaging components",
    },
    steps: {
      overline: "Launch Path",
      title: "From idea to",
      titleEm: "first order.",
      items: [
        { title: "Range planning call", text: "We map your brand positioning, target price points and first-range priorities." },
        { title: "Formula selection or brief", text: "Choose from ready-to-launch formulas, brief bespoke products, or combine both." },
        { title: "Sampling", text: "Evaluate samples, adjust fragrance, texture or actives where needed and lock the range." },
        { title: "Packaging & compliance", text: "Finalise components, labels and documentation for Indian retail and marketplaces." },
        { title: "Production & delivery", text: "First commercial batch produced through licensed partners and delivered launch-ready." },
      ],
    },
    faqs: [
      { q: "What are typical minimums for private label in India?", a: "MOQs depend on the format and facility. We work with partners that support realistic first-run quantities for new brands and will confirm numbers during range planning." },
      { q: "Can I start with stock formulas and move to custom later?", a: "Yes — that is exactly how many of our clients grow. Your custom formulas remain your brand's own." },
      { q: "Do you handle the manufacturing licence?", a: "Products are manufactured in licensed facilities within our partner network; we guide you on the documentation your brand needs to sell in India." },
    ],
    related: ["/cosmetic-manufacturing-support", "/cosmetic-formulation-development", "/regulatory-testing-support"],
  },

  "/cosmetic-manufacturing-support": {
    testId: "manufacturing-page",
    crumbs: [{ label: "Services", path: "/services" }, { label: "Manufacturing Support" }],
    hero: {
      overline: "Cosmetic Manufacturing Support",
      title: "From locked formula to",
      titleEm: "commercial production.",
      intro:
        "Scale-up, tech transfer and batch-production coordination for cosmetic, Ayurvedic and wellness products — through our network of licensed manufacturing facilities in India, with packaging and production readiness handled alongside.",
      image: LAB_LINE,
      imageAlt: "Cosmetic filling line and production",
    },
    features: {
      overline: "What's Covered",
      title: "Production support,",
      titleEm: "end to end.",
      items: [
        { icon: Layers, title: "Scale-Up & Tech Transfer", text: "Lab formulas translated into robust manufacturing procedures, with pilot batches to validate process and specification." },
        { icon: Factory, title: "Licensed Manufacturing Network", text: "Introductions and coordination with vetted, licensed facilities matched to your format, volume and category." },
        { icon: Timer, title: "Batch Production Coordination", text: "Timelines, raw-material readiness and batch approvals managed so production runs on schedule." },
        { icon: Package, title: "Packaging Compatibility", text: "Component selection, compatibility and fill-line suitability testing before you commit to tooling or stock." },
        { icon: BadgeCheck, title: "Specifications & QC", text: "Finished-product specifications, batch documentation and quality-control checkpoints defined up front." },
        { icon: Truck, title: "Production Readiness", text: "Artwork, labelling, batch coding and documentation aligned so the first batch is retail-ready." },
      ],
    },
    split: {
      overline: "Why It Matters",
      title: "Formulas fail at scale",
      titleEm: "when nobody owns the handover.",
      paragraphs: [
        "A beautiful lab sample means little if the first 500-kilogram batch separates, thickens or smells different. Our formulators stay involved through scale-up, adjusting process parameters and specifications so the commercial product matches the approved sample.",
        "Because we work with multiple licensed facilities, we can match you to the right partner for your format and volumes rather than forcing your product into a single plant's capabilities.",
      ],
      bullets: ["Pilot and validation batches", "Manufacturing procedure documentation", "Raw material and packaging sourcing guidance", "Quality-control checkpoints", "Batch record templates", "Multi-facility partner network"],
      image: U("photo-1581093577421-f561a654a353"),
      imageAlt: "Quality control in a formulation laboratory",
    },
    steps: {
      overline: "Scale-Up Path",
      title: "How we take a formula",
      titleEm: "to production.",
      items: [
        { title: "Manufacturing assessment", text: "Format, volumes, packaging and regulatory category determine the right facility and process." },
        { title: "Tech-transfer pack", text: "Formula, procedure, specifications and test methods are documented for the manufacturer." },
        { title: "Pilot batch", text: "A pilot run validates process parameters, fill behaviour and finished-product specification." },
        { title: "Commercial batch", text: "First production batch coordinated with QC checkpoints and batch documentation." },
        { title: "Ongoing supply", text: "Repeat-batch consistency, reformulation for cost or supply, and range extensions." },
      ],
    },
    faqs: [
      { q: "Do you manufacture in-house?", a: "We coordinate manufacturing through our network of licensed partner facilities, staying involved through tech transfer, pilot and commercial batches." },
      { q: "Can you transfer my formula to my own manufacturer?", a: "Yes. We prepare a full tech-transfer pack and can support your manufacturer through pilot and first production." },
      { q: "What if the scaled batch does not match the sample?", a: "Our formulators troubleshoot process and specification issues during pilot so that deviations are resolved before commercial production." },
    ],
    related: ["/private-label-cosmetics-india", "/regulatory-testing-support", "/cosmetic-formulation-development"],
  },

  "/regulatory-testing-support": {
    testId: "regulatory-page",
    crumbs: [{ label: "Services", path: "/services" }, { label: "Regulatory & Testing Support" }],
    hero: {
      overline: "Regulatory, Testing & Compliance Support",
      title: "Compliant, documented and",
      titleEm: "ready for any market.",
      intro:
        "Testing coordination, product documentation, regulatory guidance and quality documentation for cosmetic, Ayurvedic and nutraceutical products — so you can launch in India and export with confidence.",
      image: U("photo-1584308666744-24d5c474f2ae"),
      imageAlt: "Product samples prepared for laboratory testing",
    },
    features: {
      overline: "What's Covered",
      title: "Testing and paperwork,",
      titleEm: "handled.",
      items: [
        { icon: TestTubes, title: "Stability & Compatibility Testing", text: "Accelerated and real-time stability, freeze-thaw and pack compatibility studies coordinated with accredited laboratories." },
        { icon: Microscope, title: "Safety & Microbiology", text: "Preservative efficacy (challenge) testing, microbial limits, patch and safety assessments as required by your market." },
        { icon: FileText, title: "Product Documentation", text: "Formulation records, specifications, test reports and product information files organised for audits and partners." },
        { icon: ScrollText, title: "Label & Claims Review", text: "INCI listing, mandatory label elements and claims wording reviewed for Indian regulations and export destinations." },
        { icon: ClipboardCheck, title: "Regulatory Guidance", text: "Category positioning (cosmetic, Ayurvedic or nutraceutical), licensing pathways and documentation requirements explained clearly." },
        { icon: Globe2, title: "Export Compliance", text: "Documentation and formulation tuning for international markets, from ingredient restrictions to labelling formats." },
      ],
    },
    split: {
      overline: "Our Approach",
      title: "Compliance designed in,",
      titleEm: "not bolted on.",
      paragraphs: [
        "Regulatory problems are cheapest to solve at the formulation stage. We check ingredient restrictions, concentration limits and claim feasibility for your target markets before trials begin, so the finished product does not need a late redesign.",
        "As the formula is finalised, we coordinate the testing your category requires and assemble the documentation your manufacturer, marketplaces, retailers and importers will ask for.",
      ],
      bullets: ["Market-specific ingredient screening", "Accredited testing lab coordination", "Specification and test-method drafting", "Product information file assembly", "Claims substantiation guidance", "Import and export documentation"],
      image: AYURVEDA_IMG,
      imageAlt: "Laboratory glassware and botanical ingredients",
    },
    steps: {
      overline: "Compliance Path",
      title: "How we keep your product",
      titleEm: "compliant.",
      items: [
        { title: "Market & category mapping", text: "We confirm where you will sell and how the product is classified in each market." },
        { title: "Ingredient screening", text: "Restricted substances, concentration limits and labelling implications are checked before formulation." },
        { title: "Testing plan", text: "Stability, safety, microbiology and compatibility tests are scheduled with accredited laboratories." },
        { title: "Documentation", text: "Specifications, test reports, INCI and label copy are assembled into an organised product file." },
        { title: "Launch review", text: "A final review of artwork, claims and documents before the product goes on sale." },
      ],
    },
    faqs: [
      { q: "Which regulations apply to cosmetics in India?", a: "Product categories follow different regulatory pathways in India. We help you navigate product classification, applicable requirements, and documentation—from cosmetics under the Cosmetics Rules, 2020 to the relevant frameworks for Ayurvedic and nutraceutical products." },
      { q: "Do you conduct testing yourselves?", a: "We design the test plan and coordinate testing with accredited third-party laboratories, then interpret the results with you." },
      { q: "Can you help with export documentation?", a: "Yes. We prepare formulation and testing documentation aligned with the requirements of your destination markets and importers." },
    ],
    related: ["/cosmetic-manufacturing-support", "/services", "/private-label-cosmetics-india"],
  },
};
